import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useT, useI18n } from "@/context/I18nContext";

/* ------------------------------------------------------------------ */
/* Autolinking helper                                                  */
/* ------------------------------------------------------------------ */

const EMAIL_RE = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/;
const URL_RE = /(https?:\/\/[^\s)]+)/;
const INTERNAL_RE = /(\/privacy|\/terms|\/cookies|\/delete-account|\/ai-safety|\/privacy-choices)/;

/** Split a string into text + links. Supports mailto, http(s) and internal legal routes. */
function autolink(text, keyPrefix = "al") {
  if (typeof text !== "string" || !text) return text;
  // Compose a single regex so we can walk the string in order.
  const combined = new RegExp(
    `${EMAIL_RE.source}|${URL_RE.source}|${INTERNAL_RE.source}`,
    "g",
  );
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [matched] = m;
    const k = `${keyPrefix}-${i++}`;
    if (EMAIL_RE.test(matched)) {
      out.push(
        <a key={k} href={`mailto:${matched}`} className="text-sage-700 underline hover:text-ink-900" data-testid={`autolink-mail-${matched}`}>
          {matched}
        </a>,
      );
    } else if (URL_RE.test(matched)) {
      out.push(
        <a key={k} href={matched} target="_blank" rel="noopener noreferrer" className="text-sage-700 underline hover:text-ink-900">
          {matched}
        </a>,
      );
    } else {
      // Internal route — use react-router Link
      out.push(
        <Link key={k} to={matched} className="text-sage-700 underline hover:text-ink-900" data-testid={`autolink-route-${matched.slice(1)}`}>
          {matched}
        </Link>,
      );
    }
    last = m.index + matched.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/* ------------------------------------------------------------------ */
/* Head helpers (no react-helmet dependency)                           */
/* ------------------------------------------------------------------ */

function useDocumentHead({ title, description, lang }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", description);
    }
    if (lang) {
      try { document.documentElement.setAttribute("lang", lang); } catch {}
    }
  }, [title, description, lang]);
}

/* ------------------------------------------------------------------ */
/* LegalPage                                                           */
/* ------------------------------------------------------------------ */

/** Renders a legal/policy page from a structured content array:
 *  [{ heading?, subheading?, body?: string|array, list?: string[], note?: string }]
 *
 *  Props:
 *   - titleKey: i18n key for H1
 *   - updatedKey: i18n key for "Last updated" line
 *   - metaDescriptionKey: i18n key for <meta name="description">
 *   - documentTitleKey: i18n key for <title> (falls back to titleKey)
 *   - sectionsByLang: { nl: [...], en: [...] }
 *   - testId: data-testid for the main container
 */
export default function LegalPage({
  titleKey,
  updatedKey,
  metaDescriptionKey,
  documentTitleKey,
  sectionsByLang,
  testId,
}) {
  const t = useT();
  const { lang } = useI18n();
  const sections = sectionsByLang[lang] || sectionsByLang.nl || sectionsByLang.en;

  useDocumentHead({
    title: documentTitleKey ? t(documentTitleKey) : `${t(titleKey)} | Inner Compass`,
    description: metaDescriptionKey ? t(metaDescriptionKey) : undefined,
    lang,
  });

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16" data-testid={testId}>
      <header className="mb-8">
        <h1 className="font-heading text-3xl md:text-5xl tracking-tight text-ink-900">{t(titleKey)}</h1>
        {updatedKey && (
          <p className="mt-2 text-sm text-ink-500" data-testid={`${testId}-updated`}>{t(updatedKey)}</p>
        )}
      </header>

      <article className="max-w-none">
        {sections.map((s, idx) => (
          <section key={idx} className="mb-8" data-testid={`legal-section-${idx}`}>
            {s.heading && (
              <h2 className="font-heading text-xl md:text-2xl text-ink-900 mt-8 mb-3">{s.heading}</h2>
            )}
            {s.subheading && (
              <h3 className="font-heading text-base md:text-lg text-ink-900 mt-5 mb-2">{s.subheading}</h3>
            )}
            {Array.isArray(s.body) &&
              s.body.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-ink-700 leading-relaxed mb-3">
                  {autolink(para, `s${idx}-p${i}`)}
                </p>
              ))}
            {typeof s.body === "string" && (
              <p className="text-sm md:text-base text-ink-700 leading-relaxed mb-3">
                {autolink(s.body, `s${idx}-b`)}
              </p>
            )}
            {s.list && (
              <ul className="list-disc pl-5 text-sm md:text-base text-ink-700 space-y-1.5 mb-3">
                {s.list.map((li, i) => (
                  <li key={i}>{autolink(li, `s${idx}-l${i}`)}</li>
                ))}
              </ul>
            )}
            {s.note && (
              <div className="rounded-2xl bg-cream-100 border border-border p-4 text-sm text-ink-700">
                {autolink(s.note, `s${idx}-n`)}
              </div>
            )}
          </section>
        ))}
      </article>
    </div>
  );
}
